import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Link, Divider, Tooltip, IconButton, Chip, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

/**
 * MetricExplanationModal - Explains ROI metrics with corpus citations
 *
 * Provides transparency by showing:
 * 1. What the metric means (plain English definition)
 * 2. How it's calculated (the formula used)
 * 3. Corpus support (specific evidence backing the claim)
 *
 * 5-Component Corpus-Validated Model:
 * - Energy Savings: 116 PE-certified studies
 * - Avoided Degradation: ASHRAE 1998 Handbook Ch 2.9
 * - Avoided Demand: Industry kW/ton metrics
 * - Avoided Extended Runtime: 50% capacity-to-runtime factor
 * - Life Extension: 20% (roi_calculator_logic.json)
 */

const METRIC_EXPLANATIONS = {
  energy_savings: {
    title: 'Energy Savings',
    icon: '⚡',
    definition: 'Direct reduction in energy consumption from restoring equipment efficiency to near-design specifications. This is the primary, most reliably measured benefit of treatment.',
    formula: 'Annual Energy Savings = Baseline kWh × Improvement% × Electricity Rate',
    formulaExample: 'Example: 100 tons × 1.3 kW/ton × 2,500 hrs × 23.2% × $0.12 = $9,048/year',
    corpus_support: {
      source: '116 PE-certified field measurements',
      key_stat: '23.2% mean improvement across all equipment types',
      reference: 'corpus/manifest.json → stats.pe_certified_studies',
      confidence: 'HIGH - Directly measured by Professional Engineers'
    }
  },

  avoided_degradation: {
    title: 'Avoided Future Degradation',
    icon: '📉',
    definition: 'Without treatment, equipment continues losing efficiency at 2-3% per year due to oil fouling and thermal stress. Treatment stabilizes efficiency, preventing this cumulative future loss.',
    formula: 'Avoided Cost = Σ (Annual kWh × Degradation Rate × Year) × Electricity Rate',
    formulaExample: '10-year projection comparing treated (stable) vs untreated (degrading) equipment',
    corpus_support: {
      source: 'ASHRAE 1998 Handbook: Refrigeration, Chapter 2.9',
      key_stat: '7% year 1, 5% year 2, 2%/year thereafter → 28% cumulative by year 10',
      reference: 'corpus/ashrae_references.json → cumulative_calculation',
      url: 'https://archive.org/details/1998ashraehandbo00robe',
      confidence: 'HIGH - ASHRAE industry standard'
    }
  },

  avoided_demand: {
    title: 'Avoided Peak Demand Charges',
    icon: '📊',
    definition: 'Degraded equipment draws more kW to deliver the same cooling. Commercial electric bills include demand charges ($/kW) separate from energy ($/kWh), often 30-50% of the bill. Treatment reduces peak kW draw.',
    formula: 'Avoided Demand = (Degraded kW - Restored kW) × Demand Rate × 12 months',
    formulaExample: 'Each 1% efficiency loss ≈ 0.8% increase in peak kW draw',
    corpus_support: {
      source: 'Industry standard kW/ton relationship',
      key_stat: 'Degradation-to-demand factor: 0.8× (corpus validated)',
      reference: 'corpus/roi_calculator_logic.json → thermodynamic_formulas.kw_per_ton',
      confidence: 'MEDIUM - Industry standard calculation'
    }
  },

  avoided_runtime: {
    title: 'Avoided Extended Runtime',
    icon: '⏱️',
    definition: 'Degraded equipment loses cooling capacity and must run LONGER to meet the load. This is additional operating hours beyond normal, separate from efficiency loss (more kWh per hour).',
    formula: 'Avoided Runtime = (Degradation% - 15%) × 50% × Operating Hours × kWh/hour × Rate',
    formulaExample: '20% degraded → 10% longer runtime (50% capacity-to-runtime factor)',
    corpus_support: {
      source: 'Physics-based derivation from capacity loss',
      key_stat: 'Only applies above 15% degradation; uses 50% capacity-to-runtime factor',
      reference: 'Logical derivation - capacity loss requires compensating runtime',
      confidence: 'MEDIUM - Physics-based assumptions'
    },
    note: 'NOT double-counting: Energy Savings = same hours × more kWh/hour; Extended Runtime = more hours × baseline kWh/hour'
  },

  total_avoided_costs: {
    title: 'Total Avoided Costs',
    icon: '🛡️',
    definition: 'Sum of 4 avoided cost components that go BEYOND direct energy savings. These represent costs you would have incurred without treatment - degradation losses, demand charges, extended runtime, and equipment replacement.',
    formula: 'Avoided Costs = Avoided Degradation + Avoided Demand + Avoided Runtime + Life Extension',
    formulaExample: 'Typical breakdown: 40% degradation, 25% demand, 15% runtime, 20% life extension',
    corpus_support: {
      source: 'Sum of 4 corpus-validated components',
      key_stat: 'Typically 2-4× larger than direct energy savings for older equipment',
      reference: 'See individual component explanations for citations',
      confidence: 'HIGH - Each component has corpus support'
    },
    note: 'These are REAL costs avoided, not theoretical. ASHRAE documents 2-4% annual degradation; demand charges are on utility bills; extended runtime is physics-based.'
  },

  life_extension: {
    title: 'Life Extension Value (Cascading Degradation Model)',
    icon: '🔧',
    definition: 'Enhanced model capturing TWO sources of value: (1) AVOIDED ACCELERATED WEAR - degraded equipment runs longer to meet cooling load (e.g., 20% capacity loss = 25% more runtime), wearing out compressors, fans, and bearings faster and dying before expected life. Treatment restores capacity, eliminating this vicious cycle. (2) TRADITIONAL LIFE EXTENSION - 20% longer life from reduced thermal stress, deferring replacement costs.',
    formula: 'Total Value = Avoided Accelerated Wear + Traditional Life Extension\n• Extra Runtime Factor = 1/(1 - degradation%) - 1\n• Accelerated Life = Normal Life / (1 + Extra Runtime)\n• Value = NPV(Accelerated) - NPV(Normal) + NPV(Normal) - NPV(Extended)',
    formulaExample: '5-year-old chiller with 20% degradation:\n• Extra runtime: 25% (runs longer to meet load)\n• Normal life: 10 years, Accelerated: 8 years, Extended: 12 years\n• Avoided early death: ~$22K + Traditional deferral: ~$17K = ~$39K total',
    corpus_support: {
      source: 'roi_calculator_logic.json + ASHRAE equipment life data',
      key_stat: 'Captures 3-5× more value than simple 20% deferral model',
      reference: 'corpus/roi_calculator_logic.json → additional_value_factors.equipment_life_extension + ASHRAE equipment life tables',
      confidence: 'HIGH - Physics-based cascading degradation + corpus-documented 20% extension'
    },
    note: 'The cascading effect is significant: equipment that has lost capacity must run MORE HOURS to meet the same cooling load. More hours = faster wear on compressors, fan motors, contactors, and bearings. This accelerated wear shortens equipment life BEYOND normal aging.'
  },

  mission_critical_protection: {
    title: 'Mission-Critical Protection Value',
    icon: '🛡️',
    definition: 'HVAC is mission-critical infrastructure. When treatment improves reliability (35% longer MTBF), the VALUE depends on what happens when cooling fails. A data center at $50,000/hour downtime values reliability very differently than a standard office at $100/hour. This captures the operational value of avoided failures based on your facility type.',
    formula: 'Protection Value = (Failures Avoided per Year) × (Avg Repair Hours) × ($/Hour Downtime Cost)',
    formulaExample: 'Data Center with 100-ton chiller:\n• Failures avoided: 0.12/year (35% MTBF improvement)\n• Repair time: 24 hours\n• Downtime cost: $50,000/hour\n• Annual value: 0.12 × 24 × $50,000 = $144,000/year',
    corpus_support: {
      source: 'MTBF tables from corpus failure analysis + industry downtime cost data',
      key_stat: 'Varies dramatically: ~$290/year (commercial) to ~$144K/year (data center)',
      reference: 'corpus/degradationCurve.js → MTBF_BY_AGE + FACILITY_DOWNTIME_COST',
      confidence: 'HIGH - Based on documented MTBF improvement (35%) and industry downtime costs'
    },
    note: 'This value is HIGHLY dependent on facility type. Select your facility type in Company Setup to see accurate mission-critical protection value. For standard commercial buildings, this is minimal. For data centers, healthcare, pharmaceutical, or financial services, this can be the largest value component.'
  },

  total_annual_value: {
    title: 'Total Annual Value',
    icon: '💰',
    definition: 'Sum of all 6 corpus-validated value components. Represents the true comprehensive ROI, not just direct energy savings. Typically ~2× higher than energy-only calculations (can be much higher for mission-critical facilities).',
    formula: 'Total = Energy Savings + Avoided Degradation + Avoided Demand + Avoided Runtime + Life Extension + Mission-Critical Protection',
    formulaExample: 'Energy-only: ~$15K/year → 6-component: ~$29K/year (commercial) or ~$160K/year (data center)',
    corpus_support: {
      source: 'All components individually corpus-validated',
      key_stat: 'Approximately 2× higher than energy-only (much more for mission-critical)',
      reference: 'See individual component citations above',
      confidence: 'HIGH - Each component has corpus support'
    }
  },

  payback_months: {
    title: 'True Payback Period',
    icon: '📅',
    definition: 'Months until treatment cost is recovered through total value (all 6 components), not just energy savings. This provides the accurate payback timeline.',
    formula: 'Payback Months = (Treatment Cost / Total Annual Value) × 12',
    formulaExample: 'Energy-only: ~10 months → 6-component: ~5.1 months (commercial) or <1 month (data center)',
    corpus_support: {
      source: 'Calculation using all 6 corpus-validated components',
      key_stat: 'Typically 5-6 months vs 10-12 months energy-only',
      reference: 'Uses corpus-validated comprehensive value model',
      confidence: 'HIGH - Based on validated components'
    }
  },

  five_year_roi: {
    title: '5-Year ROI',
    icon: '📈',
    definition: 'Return on investment over 5 years, calculated using the comprehensive value model. Shows long-term value creation from the treatment investment.',
    formula: 'ROI = ((Total Annual Value × 5) - Treatment Cost) / Treatment Cost × 100%',
    formulaExample: '$29K/year × 5 = $145K value; ($145K - $12.5K) / $12.5K = 1,060% ROI',
    corpus_support: {
      source: '6-component corpus-validated model',
      key_stat: 'Uses total annual value, not just energy savings',
      reference: 'Compound of all corpus-validated components',
      confidence: 'HIGH - Based on validated value model'
    }
  },

  // Escalation-related metrics for Year-by-Year Projection Table
  escalation_with: {
    title: 'Value With Escalation',
    icon: '📈',
    definition: 'Total projected value over the analysis period assuming electricity prices rise at the specified rate (typically 2.5% annually). Your savings are in kWh - as prices rise, the dollar value of those kWh increases.',
    formula: 'Year N Value = Annual kWh Savings × Base Rate × (1 + Escalation%)^N',
    formulaExample: 'Year 5: 75,000 kWh × $0.12 × 1.025^5 = $10,124 (vs $9,000 flat)',
    corpus_support: {
      source: 'Historical US electricity price trends',
      key_stat: 'US commercial electricity prices rose 2-4% annually over past 20 years',
      reference: 'EIA electricity data',
      confidence: 'HIGH - Based on historical utility rate trends'
    }
  },

  escalation_without: {
    title: 'Value Without Escalation',
    icon: '📊',
    definition: 'Total projected value assuming electricity prices stay flat (no inflation). This is the floor baseline - useful for comparison but likely understates true value.',
    formula: 'Flat Value = Annual kWh Savings × Current Rate × Years',
    formulaExample: '75,000 kWh × $0.12 × 10 years = $90,000 (no price increase)',
    corpus_support: {
      source: 'Baseline calculation',
      key_stat: 'Floor assumption - prices rarely stay flat',
      reference: 'Mathematical baseline for comparison',
      confidence: 'HIGH - Simple multiplication'
    }
  },

  escalation_benefit: {
    title: 'Escalation Benefit',
    icon: '💹',
    definition: 'Additional savings value from electricity prices rising over time. Since your savings are in physical kWh units, they become worth MORE dollars as electricity prices increase. This is "free" extra value you capture.',
    formula: 'Benefit = (Value with Escalation) - (Value without Escalation)',
    formulaExample: '$100K with escalation - $90K flat = $10K escalation benefit (+11%)',
    corpus_support: {
      source: 'Historical electricity price inflation',
      key_stat: 'Typically adds 10-15% to 10-year value at 2.5% escalation',
      reference: 'Compound growth mathematics',
      confidence: 'HIGH - Mathematical certainty given price increases'
    },
    note: 'This is why locking in kWh savings NOW is valuable - you benefit from all future price increases.'
  },

  payback_year: {
    title: 'Payback Year',
    icon: '🎯',
    definition: 'The year when cumulative savings exceed treatment cost, accounting for electricity price escalation. After this year, all savings are pure profit.',
    formula: 'Payback Year = First year where Cumulative Savings > Treatment Cost',
    formulaExample: 'Treatment: $5,500 | Year 1 savings: $7,690 → Payback in Year 1',
    corpus_support: {
      source: 'ROI calculation with escalation',
      key_stat: 'Typically Year 1-2 for most equipment portfolios',
      reference: 'Cumulative savings projection',
      confidence: 'HIGH - Based on corpus-validated savings rates'
    }
  },

  co2_avoided: {
    title: 'CO2 Avoided',
    icon: '🌍',
    definition: 'Greenhouse gas emissions prevented by reducing energy consumption. Calculated using EPA grid emissions factors.',
    formula: 'CO2 Avoided = kWh Saved × 0.42 kg CO2/kWh',
    formulaExample: '75,000 kWh saved × 0.42 = 31.5 tonnes CO2/year',
    corpus_support: {
      source: 'EPA eGRID emissions factors',
      key_stat: '0.42 kg CO2/kWh (US grid average)',
      reference: 'EPA eGRID database',
      confidence: 'HIGH - Government standard'
    }
  },

  vehicle_equivalent: {
    title: 'Vehicle Equivalent',
    icon: '🚗',
    definition: 'Number of cars worth of annual emissions prevented. Helps visualize environmental impact in relatable terms.',
    formula: 'Vehicles = CO2 Tonnes / 4.6 tonnes per car per year',
    formulaExample: '31.5 tonnes / 4.6 = ~7 cars off the road for a year',
    corpus_support: {
      source: 'EPA vehicle emissions data',
      key_stat: '4.6 tonnes CO2 per average passenger vehicle per year',
      reference: 'EPA Greenhouse Gas Equivalencies Calculator',
      confidence: 'HIGH - Government standard'
    }
  },

  // ==========================================================================
  // PAYMENT METRICS (Monthly Cash Flow Focus)
  // ==========================================================================

  monthly_payment: {
    title: 'Monthly Payment',
    icon: '💳',
    definition: 'Your monthly outlay under the 3-Year Payment Program. Total treatment cost divided by 36 months. No interest charges - same total cost as paying upfront.',
    formula: 'Monthly Payment = Treatment Cost ÷ 36 months',
    formulaExample: '$12,500 ÷ 36 = $347/month',
    corpus_support: {
      source: 'PermaFrost 3-Year Payment Program',
      key_stat: 'Zero interest - same total cost as upfront',
      reference: 'Payment program terms',
      confidence: 'HIGH - Program terms'
    }
  },

  monthly_savings: {
    title: 'Monthly Savings',
    icon: '💰',
    definition: 'Your monthly energy cost reduction from restored equipment efficiency. This is the cash you keep each month from reduced utility bills.',
    formula: 'Monthly Savings = Annual Savings ÷ 12',
    formulaExample: '$29,000/year ÷ 12 = $2,417/month',
    corpus_support: {
      source: '116 PE-certified field measurements',
      key_stat: '23.2% average efficiency improvement',
      reference: 'corpus/manifest.json → mean_improvement',
      confidence: 'HIGH - Directly measured'
    }
  },

  monthly_cash_flow: {
    title: 'Monthly Net Cash Flow',
    icon: '📈',
    definition: 'Your net monthly position: savings minus payment. A positive number means the treatment pays for itself each month - no out-of-pocket cost. The energy savings fund the treatment.',
    formula: 'Monthly Net = Monthly Savings - Monthly Payment',
    formulaExample: '$2,417 savings - $347 payment = +$2,070/month positive',
    corpus_support: {
      source: 'Cash flow calculation',
      key_stat: 'Most portfolios are cash-positive from month 1',
      reference: 'paymentCalculations.js → monthlyCashFlow',
      confidence: 'HIGH - Mathematical calculation'
    }
  },

  year1_outlay: {
    title: 'Year 1 Cash Outlay',
    icon: '💸',
    definition: 'Total cash required in year 1 to fund the treatment. With upfront payment, this equals the full treatment cost. With 3-year deferred, this is only 1/3 of total cost.',
    formula: 'Upfront: Full Treatment Cost | 3-Year: Treatment Cost ÷ 3',
    formulaExample: '$12,500 upfront vs $4,167/year for 3 years',
    corpus_support: {
      source: 'PermaFrost payment program terms',
      key_stat: '3-year program has no interest charges',
      reference: 'Payment program documentation',
      confidence: 'HIGH - Program terms'
    }
  },

  year1_net_benefit: {
    title: 'Year 1 Net Benefit',
    icon: '📊',
    definition: 'Net cash position at end of year 1: total annual savings minus year 1 payment. Positive value means cash-flow positive from year 1.',
    formula: 'Year 1 Net = Annual Savings - Year 1 Payment',
    formulaExample: '3-Year: $29K savings - $4.2K payment = $24.8K net positive',
    corpus_support: {
      source: 'Cash flow analysis',
      key_stat: '3-year program typically cash-positive from month 1',
      reference: 'paymentCalculations.js → year1NetBenefit',
      confidence: 'HIGH - Mathematical calculation'
    }
  },

  five_year_cumulative_value: {
    title: '5-Year Cumulative Value',
    icon: '📊',
    definition: 'Total value generated over 5 years from all 5 corpus-validated components. This represents the comprehensive benefit, not just direct energy savings.',
    formula: '5-Year Value = Total Annual Value × 5',
    formulaExample: '$7,690/year × 5 = $38,449 total value over 5 years',
    corpus_support: {
      source: 'Sum of all 5 value components over 5 years',
      key_stat: 'Includes energy savings + all avoided costs',
      reference: 'easCalculations.js → year_5_cumulative_value',
      confidence: 'HIGH - Each annual component is corpus-validated'
    },
    note: 'Compare to "energy savings only" to see how much additional value comes from avoided costs.'
  },

  five_year_net_profit: {
    title: '5-Year Net Profit',
    icon: '💰',
    definition: 'Total profit over 5 years after subtracting treatment cost. This is your actual return - value generated minus what you paid.',
    formula: '5-Year Net = (Total Annual Value × 5) - Treatment Cost',
    formulaExample: '$38,449 value - $5,500 treatment = $32,949 net profit',
    corpus_support: {
      source: '5-year projection calculation',
      key_stat: 'Same outcome regardless of payment option (no interest)',
      reference: 'easCalculations.js → year_5_net',
      confidence: 'HIGH - Mathematical calculation'
    }
  },

  capex_vs_opex: {
    title: 'CapEx vs OpEx Treatment',
    icon: '🏦',
    definition: 'How the expense is categorized for accounting. Upfront payment is often capital expenditure (CapEx) requiring depreciation. 3-year deferred may qualify as operating expense (OpEx) for immediate tax deduction.',
    formula: 'CapEx: Depreciate over useful life | OpEx: Expense immediately',
    formulaExample: '$12.5K upfront CapEx vs $4.2K/yr OpEx',
    corpus_support: {
      source: 'Standard accounting treatment',
      key_stat: 'Consult your tax advisor for specific treatment',
      reference: 'General accounting principles',
      confidence: 'MEDIUM - Varies by jurisdiction and company'
    },
    note: 'Tax treatment depends on your specific circumstances. Consult a tax professional.'
  },

  budget_approval: {
    title: 'Budget Approval Level',
    icon: '✅',
    definition: 'The organizational approval level required. Capital expenditures typically require higher-level approval (CFO, board). Operating expenses within department budgets may require only manager approval.',
    formula: 'CapEx: C-suite/Board approval | OpEx: Manager/Director approval',
    formulaExample: 'Capital: Board approval for $12.5K | Operating: Manager approval for $4.2K',
    corpus_support: {
      source: 'Standard corporate governance',
      key_stat: 'Lower dollar amounts typically need less approval',
      reference: 'Corporate approval hierarchies',
      confidence: 'MEDIUM - Varies by organization'
    }
  },

  cash_flow_positive_day_one: {
    title: 'Cash Flow Positive from Day One',
    icon: '✨',
    definition: 'With the 3-year deferred program, monthly energy savings typically exceed monthly payments, resulting in positive cash flow from the first month. No upfront capital required - the treatment funds itself.',
    formula: 'If (Monthly Savings > Monthly Payment) → Cash Flow Positive',
    formulaExample: '$2,417/mo savings > $347/mo payment = $2,070 positive from day 1',
    corpus_support: {
      source: 'Payment program cash flow analysis',
      key_stat: 'Most portfolios achieve positive cash flow immediately',
      reference: 'paymentCalculations.js → isCashFlowPositiveMonthly',
      confidence: 'HIGH - Demonstrated in calculator'
    }
  },

  no_payback_needed: {
    title: 'No Payback Period',
    icon: '🎯',
    definition: 'With the 3-Year Payment Program, there is NO payback period because your monthly savings ALWAYS exceed your monthly payment. You profit every single month from day 1 through year 3 and beyond. Unlike upfront payment where you spend first then recover, the 3-year option means you never go negative.',
    formula: 'Savings ÷ Payment = Profit Multiple (typically 3-5×)',
    formulaExample: '$644/mo savings ÷ $153/mo payment = 4.2× more savings than cost → you keep $491/mo profit',
    corpus_support: {
      source: 'Payment program cash flow analysis',
      key_stat: 'Savings typically exceed payments by 60-300%+',
      reference: 'paymentCalculations.js → monthlyCashFlow always positive',
      confidence: 'HIGH - Mathematical certainty when savings > payments'
    },
    note: 'Upfront payment HAS a payback period (~8-10 months). The 3-Year option has NO payback because you profit every period.'
  },

  // ==========================================================================
  // CFO FINANCIAL METRICS (Executive Dashboard)
  // ==========================================================================

  ebitda_impact: {
    title: 'EBITDA Impact',
    icon: '📊',
    definition: 'Energy savings flow directly to EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) dollar-for-dollar. Unlike revenue which must cover costs, every $1 saved in energy costs adds $1 to EBITDA.',
    formula: 'EBITDA Impact = Annual Energy Savings (direct contribution)',
    formulaExample: '$29,000/year in energy savings = $29,000/year EBITDA improvement',
    corpus_support: {
      source: 'Standard financial accounting principle',
      key_stat: 'Cost reductions are 100% EBITDA contribution',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.ebitda',
      confidence: 'HIGH - Standard financial accounting'
    }
  },

  ebitda_margin: {
    title: 'EBITDA Margin Improvement',
    icon: '📈',
    definition: 'The percentage increase in EBITDA margin resulting from energy savings. Shows how the energy savings translate to improved profitability as a percentage of revenue.',
    formula: 'Margin Improvement = (Annual Savings / Company Revenue) × 100',
    formulaExample: '$29K savings ÷ $50M revenue = 0.058% margin improvement',
    corpus_support: {
      source: 'Derived from company-provided revenue data',
      key_stat: 'Typically 0.01-0.10% for mid-size companies',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.margin',
      confidence: 'MEDIUM - Depends on accurate revenue input'
    }
  },

  revenue_equivalent: {
    title: 'Revenue Equivalent',
    icon: '💵',
    definition: 'The amount of new revenue that would be required to generate the same bottom-line profit as the energy savings. Because revenue must cover costs, $1 in cost savings equals $10-$20 in new revenue at typical profit margins.',
    formula: 'Revenue Equivalent = Annual Savings ÷ Net Profit Margin',
    formulaExample: '$29K savings ÷ 5% margin = $580,000 equivalent new revenue',
    corpus_support: {
      source: 'Standard financial analysis',
      key_stat: 'Default 5% profit margin (industry typical)',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.revenue_equivalence',
      confidence: 'HIGH - Standard business metric'
    }
  },

  market_cap_impact: {
    title: 'Market Cap / Enterprise Value Impact',
    icon: '🏢',
    definition: 'Estimated increase in company valuation based on EBITDA improvement. Public companies trade at a multiple of EBITDA; improving EBITDA by $1 can increase market cap by $8-15 depending on industry multiple.',
    formula: 'Valuation Impact = EBITDA Improvement × EBITDA Multiple',
    formulaExample: '$29K EBITDA × 8x multiple = $232,000 valuation increase',
    corpus_support: {
      source: 'Standard M&A valuation methodology',
      key_stat: 'Default 8x EBITDA multiple (industry typical)',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.valuation',
      confidence: 'MEDIUM - Actual multiples vary by industry'
    }
  },

  npv: {
    title: 'Net Present Value (NPV)',
    icon: '💰',
    definition: 'The present value of all future cash flows (savings) minus the initial investment, discounted at the company\'s cost of capital (WACC). A positive NPV means the investment creates value.',
    formula: 'NPV = Σ(Annual Savings ÷ (1 + WACC)^year) - Treatment Cost',
    formulaExample: '$29K/yr × 7.6 PV factor (10% WACC, 15 yrs) - $12.5K = $208K NPV',
    corpus_support: {
      source: 'Standard capital budgeting methodology',
      key_stat: 'Default 10% WACC, 15-year equipment life',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.npv',
      confidence: 'HIGH - Standard finance calculation'
    }
  },

  irr: {
    title: 'Internal Rate of Return (IRR)',
    icon: '📈',
    definition: 'The discount rate at which the NPV equals zero. Represents the annualized return on the investment. Compare to cost of capital (WACC) - IRR should exceed WACC for value creation.',
    formula: 'Solve for r: 0 = Σ(Annual Savings ÷ (1 + r)^year) - Treatment Cost',
    formulaExample: 'Treatment $12.5K, $29K/year for 15 years → IRR ≈ 230%',
    corpus_support: {
      source: 'Standard capital budgeting methodology',
      key_stat: 'Typical IRR: 150-300% for PermaFrost treatments',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.irr',
      confidence: 'HIGH - Standard finance calculation'
    }
  },

  roic: {
    title: 'Return on Invested Capital (ROIC)',
    icon: '🔄',
    definition: 'Annual after-tax return divided by the treatment investment. Shows how efficiently each dollar invested generates returns. Compare to cost of capital for value creation assessment.',
    formula: 'ROIC = (Annual Savings × (1 - Tax Rate)) ÷ Treatment Cost',
    formulaExample: '($29K × 0.75) ÷ $12.5K = 174% ROIC',
    corpus_support: {
      source: 'Standard capital efficiency metric',
      key_stat: 'Assumes 25% effective tax rate',
      reference: 'corpus/roi_calculator_logic.json → cfo_metrics.roic',
      confidence: 'HIGH - Standard finance calculation'
    }
  },

  // ==========================================================================
  // REIT-SPECIFIC METRICS
  // ==========================================================================

  noi_improvement: {
    title: 'NOI Improvement',
    icon: '🏠',
    definition: 'Net Operating Income increase from reduced energy costs. For REITs, NOI directly drives property valuation. Energy savings flow dollar-for-dollar to NOI.',
    formula: 'NOI Improvement = Annual Energy Savings',
    formulaExample: '$29,000/year energy savings = $29,000/year NOI increase',
    corpus_support: {
      source: 'REIT financial reporting standards',
      key_stat: 'Direct operating expense reduction',
      reference: 'corpus/roi_calculator_logic.json → reit_metrics.noi',
      confidence: 'HIGH - Standard REIT accounting'
    }
  },

  property_value_increase: {
    title: 'Property Value Increase',
    icon: '📈',
    definition: 'Estimated increase in property value based on NOI improvement and cap rate. Property Value = NOI ÷ Cap Rate, so NOI increases drive proportional value increases.',
    formula: 'Property Value Increase = NOI Improvement ÷ Cap Rate',
    formulaExample: '$29K NOI ÷ 8% cap rate = $362,500 property value increase',
    corpus_support: {
      source: 'Commercial real estate valuation',
      key_stat: 'Default 8% cap rate (industry standard)',
      reference: 'corpus/roi_calculator_logic.json → reit_metrics.property_value',
      confidence: 'MEDIUM - Cap rates vary by market'
    }
  },

  ffo_impact: {
    title: 'FFO/AFFO Impact',
    icon: '💵',
    definition: 'Funds From Operations impact. FFO is the primary performance metric for REITs, similar to earnings for regular companies. Energy savings directly increase FFO.',
    formula: 'FFO Impact = Annual Energy Savings (direct contribution)',
    formulaExample: '$29,000/year savings = $29,000/year FFO increase',
    corpus_support: {
      source: 'NAREIT FFO definition',
      key_stat: 'Energy cost reduction = direct FFO improvement',
      reference: 'NAREIT guidelines',
      confidence: 'HIGH - Standard REIT metric'
    }
  },

  // ==========================================================================
  // EQUIPMENT TYPE METRICS
  // ==========================================================================

  per_type_payback: {
    title: 'Payback by Equipment Type',
    icon: '⏱️',
    definition: 'Average payback period for this equipment category. Different equipment types have different efficiency improvement rates and operating characteristics, affecting payback.',
    formula: 'Type Payback = Type Treatment Cost ÷ Type Annual Value',
    formulaExample: 'Chillers: 5.0mo, RTUs: 4.2mo, CRACs: 5.5mo (typical)',
    corpus_support: {
      source: '116 PE-certified field measurements by type',
      key_stat: 'Payback varies by equipment operating hours',
      reference: 'corpus/manifest.json → equipment_type_statistics',
      confidence: 'HIGH - Corpus data by equipment type'
    }
  },

  per_type_roi: {
    title: '5-Year ROI by Equipment Type',
    icon: '📈',
    definition: 'Return on investment over 5 years for this equipment category. Varies by equipment type due to different operating hours, efficiency improvements, and energy consumption patterns.',
    formula: 'Type 5-Year ROI = ((Annual Value × 5) - Treatment Cost) ÷ Treatment Cost × 100%',
    formulaExample: 'RTUs (high hours): 1,200% | Seasonal: 800%',
    corpus_support: {
      source: 'Equipment-specific corpus analysis',
      key_stat: 'Operating hours drive ROI differences',
      reference: 'corpus/equipment_statistics.json',
      confidence: 'HIGH - Calculated from corpus data'
    }
  },

  per_type_savings: {
    title: 'Annual Savings by Type',
    icon: '💵',
    definition: 'Total annual energy savings for all equipment of this type. Based on type-specific efficiency improvement percentages and operating characteristics.',
    formula: 'Type Savings = Σ(Unit Tonnage × Hours × kW/ton × Improvement% × Rate)',
    formulaExample: '3 Chillers: $36K + $28K + $22K = $86K/year',
    corpus_support: {
      source: '116 PE-certified measurements',
      key_stat: 'Type-specific improvement percentages',
      reference: 'EQUIPMENT_STATS in corpusStatistics.js',
      confidence: 'HIGH - Direct corpus data'
    }
  },

  // ==========================================================================
  // ROI CALCULATOR LIFETIME METRICS
  // ==========================================================================

  corpus_vs_ashrae: {
    title: 'Corpus vs ASHRAE Degradation Model',
    icon: '🔬',
    definition: 'Comparison of two efficiency degradation models. ASHRAE uses theoretical 2%/year. Corpus model uses 82 actual field measurements showing real-world degradation is typically higher.',
    formula: 'ASHRAE: 2% × Years | Corpus: Empirical regression from 82 studies',
    formulaExample: 'At 10 years: ASHRAE = 20% | Corpus = 26.3% (real-world)',
    corpus_support: {
      source: '82 PE-certified before/after measurements',
      key_stat: 'Real degradation typically 25-35% higher than ASHRAE',
      reference: 'corpus/age_efficiency_analysis.json',
      confidence: 'HIGH - Measured vs theoretical'
    }
  },

  preventive_roi: {
    title: 'Preventive ROI',
    icon: '🛡️',
    definition: 'Return from preventing FUTURE degradation. Best for new equipment (0-3 years). Treatment locks in efficiency before degradation occurs, preventing years of cumulative losses.',
    formula: 'Preventive ROI = (Prevented Degradation Cost over Life) ÷ Treatment Cost',
    formulaExample: 'New chiller: Prevents $180K in degradation over 20 years = 1,440% ROI',
    corpus_support: {
      source: 'ASHRAE 1998 Handbook degradation curves',
      key_stat: '2-3%/year degradation prevented = $15K-$25K/year value',
      reference: 'corpus/ashrae_references.json → degradation_prevention',
      confidence: 'HIGH - ASHRAE industry standard'
    }
  },

  restorative_roi: {
    title: 'Restorative ROI',
    icon: '🔧',
    definition: 'Return from restoring EXISTING degradation. Best for older equipment (10+ years). Treatment restores efficiency lost over years of operation, providing immediate savings.',
    formula: 'Restorative ROI = (Restored Efficiency Savings × Remaining Life) ÷ Treatment Cost',
    formulaExample: '15-year chiller: Restores 28% efficiency × 10 remaining years = 890% ROI',
    corpus_support: {
      source: '116 PE-certified efficiency improvement studies',
      key_stat: '23.2% average efficiency restoration (all types)',
      reference: 'corpus/manifest.json → mean_improvement',
      confidence: 'HIGH - Directly measured'
    }
  },

  lifetime_savings: {
    title: 'Lifetime Savings',
    icon: '💰',
    definition: 'Total savings over the remaining useful life of the equipment. Combines annual savings with expected remaining years of operation based on ASHRAE equipment life guidelines.',
    formula: 'Lifetime Savings = Annual Savings × Remaining Life Years',
    formulaExample: '$29K/year × 12 remaining years = $348K lifetime savings',
    corpus_support: {
      source: 'ASHRAE equipment life data + corpus efficiency data',
      key_stat: 'Chiller median life: 20 years, RTU: 15 years',
      reference: 'ASHRAE Median Equipment Service Lives',
      confidence: 'HIGH - ASHRAE standard + corpus data'
    }
  },

  // Additional CFO Metrics
  enterprise_value: {
    title: 'Enterprise Value Impact',
    icon: '🏢',
    definition: 'The increase in company valuation resulting from improved EBITDA. Investors value companies as a multiple of earnings, so recurring savings directly increase the market value of your business.',
    formula: 'Enterprise Value Impact = EBITDA Impact × EBITDA Multiple',
    formulaExample: '$22K EBITDA × 8x multiple = $176K enterprise value increase',
    corpus_support: {
      source: 'Standard business valuation methodology',
      key_stat: 'Typical EBITDA multiples: 6-10x depending on industry',
      reference: 'Corporate finance valuation principles',
      confidence: 'HIGH - Standard valuation methodology'
    },
    note: 'For public companies, this translates to market cap impact. For private companies, this affects sale price and borrowing capacity.'
  },

  new_equipment_roi: {
    title: 'Why Treat New Equipment?',
    icon: '🆕',
    definition: 'Treating equipment at Year 0 captures maximum lifetime value by preventing degradation before it starts. Every year of delay loses both that year\'s prevention AND reduces remaining equipment life.',
    formula: `Lifetime Value = Σ (prevented degradation × rate × hours × tons)

Year 0: Full 23-year prevention = $516K
Year 10: Only 13 years left = $350K + $280K already wasted`,
    formulaExample: `100-ton Chiller, $0.12/kWh, 2,500 hrs/year, 23-year life:

Year 0 Treatment:
  • Prevents 16-26% degradation for 23 years
  • Lifetime value: $516,000
  • ROI: 1,960%

Year 10 Treatment:
  • Restores 21% for only 13 remaining years
  • Lifetime value: $350,000
  • Already wasted: $280,000
  • ROI: 1,450%

Delay cost: $166,000 lost value`,
    corpus_support: {
      source: 'Degradation Curve Analysis',
      key_stat: 'Equipment degrades 2-3%/year (ASHRAE), reaching 21-27% by year 10-15',
      reference: 'degradationCurve.js + 82 PE-certified field measurements',
      confidence: 'HIGH'
    },
    note: 'This is why new construction and commissioning represent optimal treatment timing.'
  }
};

// Confidence level colors (dark mode / light mode)
const CONFIDENCE_COLORS_DARK = {
  'HIGH': '#4ECDC4',    // Teal
  'MEDIUM': '#C5E87D',  // Lime
  'LOW': '#FF6B35'      // Orange
};
const CONFIDENCE_COLORS_LIGHT = {
  'HIGH': '#1a1a1a',
  'MEDIUM': '#1a1a1a',
  'LOW': '#1a1a1a'
};

/**
 * Generate dynamic formula examples based on actual calculated values
 * Falls back to null if values not provided (static example will be used)
 */
const getDynamicExample = (metric, values) => {
  if (!values || Object.keys(values).length === 0) return null;

  // Format helpers
  const formatK = (n) => {
    if (n === undefined || n === null) return null;
    const k = Math.round(n / 1000);
    return k >= 1 ? `$${k}K` : `$${Math.round(n)}`;
  };

  const formatPct = (n) => {
    if (n === undefined || n === null) return null;
    return `${(n * 100).toFixed(1)}%`;
  };

  switch (metric) {
    case 'ebitda_impact':
      if (values.ebitda) {
        return `${formatK(values.ebitda)} annual savings = ${formatK(values.ebitda)} EBITDA contribution`;
      }
      break;

    case 'revenue_equivalent':
      if (values.ebitda && values.margin && values.revenueEquiv) {
        return `${formatK(values.ebitda)} savings ÷ ${formatPct(values.margin)} margin = ${formatK(values.revenueEquiv)} revenue equivalent`;
      }
      break;

    case 'enterprise_value':
      if (values.ebitda && values.multiple && values.enterpriseValue) {
        return `${formatK(values.ebitda)} EBITDA × ${values.multiple}x multiple = ${formatK(values.enterpriseValue)} enterprise value increase`;
      }
      break;

    case 'npv':
      if (values.npv && values.wacc) {
        return `NPV at ${formatPct(values.wacc)} WACC = ${formatK(values.npv)}`;
      }
      break;

    case 'irr':
      if (values.irr !== undefined) {
        const irrPct = values.irr * 100;
        return irrPct > 1000
          ? `IRR exceeds 1000% (extremely high return)`
          : `IRR = ${irrPct.toFixed(0)}% annual return on investment`;
      }
      break;

    case 'five_year_roi':
      if (values.fiveYearRoi !== undefined) {
        return `5-Year ROI = ${(values.fiveYearRoi * 100).toFixed(0)}% total return`;
      }
      break;

    default:
      return null;
  }
  return null;
};

/**
 * MetricExplanationModal Component
 *
 * @param {string} metric - Key from METRIC_EXPLANATIONS
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Handler when modal closes
 * @param {Object} values - Optional actual values for dynamic examples
 */
export default function MetricExplanationModal({ metric, open, onClose, values = {} }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const explanation = METRIC_EXPLANATIONS[metric];
  const dynamicExample = getDynamicExample(metric, values);

  if (!explanation) {
    return null;
  }

  const confMap = isDark ? CONFIDENCE_COLORS_DARK : CONFIDENCE_COLORS_LIGHT;
  const confidenceColor = confMap[explanation.corpus_support.confidence?.split(' - ')[0]] || confMap.MEDIUM;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(26, 31, 37, 0.98) 0%, rgba(15, 20, 25, 0.99) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.99) 100%)',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography sx={{ fontSize: '1.5rem' }}>{explanation.icon}</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {explanation.title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Definition Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: isDark ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? 'primary.main' : 'text.primary' }}>
              What It Means
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {explanation.definition}
          </Typography>
          {explanation.note && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                p: 1,
                borderRadius: 1,
                background: 'rgba(78, 205, 196, 0.1)',
                border: '1px solid rgba(78, 205, 196, 0.2)',
                color: isDark ? '#4ECDC4' : 'text.primary'
              }}
            >
              Note: {explanation.note}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2, borderColor: 'divider' }} />

        {/* Formula Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalculateOutlinedIcon sx={{ fontSize: 18, color: isDark ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? 'primary.main' : 'text.primary' }}>
              How It's Calculated
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              background: 'rgba(0, 0, 0, 0.2)',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.85rem',
              color: isDark ? '#C5E87D' : 'text.primary'
            }}
          >
            {explanation.formula}
          </Box>
          {(dynamicExample || explanation.formulaExample) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {dynamicExample || explanation.formulaExample}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2, borderColor: 'divider' }} />

        {/* Corpus Support Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <VerifiedOutlinedIcon sx={{ fontSize: 18, color: isDark ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? 'primary.main' : 'text.primary' }}>
              Corpus Evidence
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              background: 'rgba(0, 0, 0, 0.15)',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
            }}
          >
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Source</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {explanation.corpus_support.source}
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Key Finding</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: confidenceColor,
                  fontFamily: '"JetBrains Mono", monospace'
                }}
              >
                {explanation.corpus_support.key_stat}
              </Typography>
            </Box>

            <Box sx={{ mb: explanation.corpus_support.url ? 1.5 : 0 }}>
              <Typography variant="caption" color="text.secondary">Reference</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}
              >
                {explanation.corpus_support.reference}
              </Typography>
            </Box>

            {explanation.corpus_support.url && (
              <Box>
                <Typography variant="caption" color="text.secondary">External Link</Typography>
                <Typography variant="body2">
                  <Link
                    href={explanation.corpus_support.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: isDark ? '#4ECDC4' : 'text.primary', fontSize: '0.8rem' }}
                  >
                    View Source →
                  </Link>
                </Typography>
              </Box>
            )}

            {/* Confidence Rating Section */}
            <Box sx={{ mt: 2, pt: 1.5, borderTop: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` }}>
              {/* Title with info tooltip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Confidence Rating
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        What do confidence ratings mean?
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, lineHeight: 1.4 }}>
                        <strong>HIGH:</strong> Uses standard accounting formulas with no assumptions required.
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                        <strong>MEDIUM:</strong> Uses reasonable defaults that may vary by company or industry.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <IconButton size="small" sx={{ p: 0.25, color: isDark ? 'rgba(255,255,255,0.4)' : 'text.disabled' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Confidence Badge */}
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  background: `${confidenceColor}22`,
                  border: `1px solid ${confidenceColor}44`,
                  color: confidenceColor,
                  fontWeight: 600
                }}
              >
                {explanation.corpus_support.confidence}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Export the metrics list for use by other components
export { METRIC_EXPLANATIONS };
